import {
  formatDateLocalized,
  formatDateTime,
  type FormatDateTimeOptions,
} from '../../../config/appConfig';
import { getBrowserTimeZone, formatTimeZoneLabel, formatTimeZoneOffset } from '../../../utils/timeZone';
import type { EmailDateHintMailboxRow, EmailDateHintRow } from './emailDateHint';

export interface LastSyncAccountSnapshot {
  id: number;
  emailAddress: string;
  lastSyncAt?: string | null;
  syncStatus?: string | null;
}

export type LastSyncStatus = 'empty' | 'never' | 'syncing' | 'ok';

export interface LastSyncInfo {
  status: LastSyncStatus;
  timeText: string;
  lastSyncAt?: string;
  latestAccountEmail?: string;
  accountsSnapshot: LastSyncAccountSnapshot[];
  subtitle?: string;
  syncing: boolean;
  ariaLabel: string;
}

export interface LastSyncHintContent {
  primaryTime: string;
  primaryZoneLabel: string;
  primaryOffset: string;
  zoneCaption: string;
  otherZones: EmailDateHintRow[];
  mailboxRows?: EmailDateHintMailboxRow[];
}

export function formatLastSyncDisplay(
  at: string,
  options?: FormatDateTimeOptions,
): string {
  const dateObj = new Date(at);
  if (Number.isNaN(dateObj.getTime())) return '';

  return formatDateLocalized(
    dateObj,
    {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
    options,
  );
}

function formatInZone(date: string, zone: string, formatOpts?: FormatDateTimeOptions): string {
  return formatDateTime(date, { ...formatOpts, timeZone: zone });
}

export interface BuildLastSyncHintContentOptions {
  zoneCaption: string;
  effectiveZone: string;
  displayPreference?: string;
  lastSyncAt: string;
  formatOpts?: FormatDateTimeOptions;
  accounts: LastSyncAccountSnapshot[];
}

export function buildLastSyncHintContent({
  zoneCaption,
  effectiveZone,
  displayPreference,
  lastSyncAt,
  formatOpts,
  accounts,
}: BuildLastSyncHintContentOptions): LastSyncHintContent {
  const browserZone = getBrowserTimeZone();
  const at = new Date(lastSyncAt);
  const otherZones: EmailDateHintRow[] = [];
  const seenZones = new Set<string>([effectiveZone]);

  const addOtherZone = (id: string, zone: string, label: string, description: string) => {
    if (seenZones.has(zone)) return;
    seenZones.add(zone);
    const time = formatInZone(lastSyncAt, zone, formatOpts);
    if (!time) return;
    otherZones.push({
      id,
      label,
      time,
      offset: formatTimeZoneOffset(zone, at),
      description,
    });
  };

  addOtherZone('utc', 'UTC', 'UTC', 'Coordinated Universal Time');

  if (browserZone !== effectiveZone) {
    addOtherZone(
      'browser',
      browserZone,
      formatTimeZoneLabel(browserZone),
      displayPreference ? 'Your computer timezone' : 'Browser default',
    );
  }

  const uniqueAccounts = accounts.filter(
    (account, index, list) => list.findIndex((other) => other.id === account.id) === index,
  );

  const mailboxRows =
    uniqueAccounts.length > 1
      ? [...uniqueAccounts]
          .sort((a, b) => a.emailAddress.localeCompare(b.emailAddress, undefined, { sensitivity: 'base' }))
          .map((account) => {
            const syncing = account.syncStatus === 'syncing';
            const time = account.lastSyncAt
              ? formatInZone(account.lastSyncAt, effectiveZone, formatOpts)
              : syncing
                ? 'Syncing…'
                : 'Never';
            return {
              id: String(account.id),
              label: account.emailAddress,
              time,
              description: syncing ? 'Sync in progress' : account.lastSyncAt ? 'Last successful sync' : 'Not synced yet',
            };
          })
      : undefined;

  const primaryZoneLabel = displayPreference
    ? formatTimeZoneLabel(displayPreference)
    : formatTimeZoneLabel(browserZone);

  return {
    primaryTime: formatInZone(lastSyncAt, effectiveZone, formatOpts) || formatLastSyncDisplay(lastSyncAt, formatOpts),
    primaryZoneLabel,
    primaryOffset: formatTimeZoneOffset(effectiveZone, at),
    zoneCaption,
    otherZones,
    mailboxRows,
  };
}

export interface ResolveLastSyncInput {
  id: number;
  emailAddress: string;
  lastSyncAt?: string | null;
  syncStatus?: string | null;
}

export function resolveLastSyncInfo(accounts: ResolveLastSyncInput[]): LastSyncInfo {
  const uniqueAccounts = accounts.filter(
    (account, index, list) => list.findIndex((other) => other.id === account.id) === index,
  );
  const accountsSnapshot: LastSyncAccountSnapshot[] = uniqueAccounts.map((account) => ({
    id: account.id,
    emailAddress: account.emailAddress,
    lastSyncAt: account.lastSyncAt,
    syncStatus: account.syncStatus,
  }));

  if (uniqueAccounts.length === 0) {
    return {
      status: 'empty',
      timeText: '—',
      accountsSnapshot,
      syncing: false,
      ariaLabel: 'Last sync unavailable',
    };
  }

  const syncing = uniqueAccounts.some((a) => a.syncStatus === 'syncing');
  const withSync = uniqueAccounts.filter((a) => a.lastSyncAt);

  if (withSync.length === 0) {
    return {
      status: syncing ? 'syncing' : 'never',
      timeText: syncing ? 'In progress…' : 'Never',
      accountsSnapshot,
      syncing,
      ariaLabel: syncing ? 'Sync in progress' : 'Never synced',
    };
  }

  let latest = withSync[0];
  let latestTime = new Date(latest.lastSyncAt!).getTime();
  for (let i = 1; i < withSync.length; i++) {
    const t = new Date(withSync[i].lastSyncAt!).getTime();
    if (t > latestTime) {
      latest = withSync[i];
      latestTime = t;
    }
  }

  const subtitle =
    uniqueAccounts.length > 1 ? `Latest among ${uniqueAccounts.length} mailboxes` : undefined;

  return {
    status: syncing ? 'syncing' : 'ok',
    timeText: syncing ? 'Syncing…' : '',
    lastSyncAt: latest.lastSyncAt!,
    latestAccountEmail: latest.emailAddress,
    accountsSnapshot,
    subtitle,
    syncing,
    ariaLabel: syncing
      ? 'Mailbox sync in progress'
      : `Last sync${uniqueAccounts.length > 1 ? `, ${latest.emailAddress}` : ''}`,
  };
}
